/**
 * Sponsor page for the chest-token reserve.
 *
 * Mirrors `sponsor-pool-page.ts` but writes to `/api/chest-reserve/*`
 * instead of the arena pool. The on-chain transfer is identical (same
 * `paySponsorPoolNative` helper); only the server bucket differs.
 */

import {
  fetchChestReserveConfig,
  paySponsorPoolNative,
  verifyChestReservePayment,
  type ChestReserveConfig,
} from './entry-fee.js';

function formatRaw(raw: bigint, decimals: number, maxFraction = 4): string {
  const scale = 10n ** BigInt(decimals);
  const whole = raw / scale;
  const frac = raw % scale;
  if (frac === 0n) return whole.toString();
  const padded = frac.toString().padStart(decimals, '0');
  const trimmed = padded.slice(0, maxFraction).replace(/0+$/, '');
  return trimmed ? `${whole}.${trimmed}` : whole.toString();
}

function parseTokenAmount(value: string, decimals: number): bigint | null {
  const trimmed = value.trim();
  if (!/^\d+(\.\d+)?$/.test(trimmed)) return null;
  const [wholeRaw, fracRaw = ''] = trimmed.split('.');
  if (fracRaw.length > decimals) return null;
  const whole = BigInt(wholeRaw);
  const frac = BigInt(fracRaw.padEnd(decimals, '0') || '0');
  return whole * (10n ** BigInt(decimals)) + frac;
}

function usdLabel(usdMicro: bigint): string {
  const whole = usdMicro / 1_000_000n;
  const cents = (usdMicro % 1_000_000n) / 10_000n;
  return cents === 0n ? `$${whole}` : `$${whole}.${cents.toString().padStart(2, '0')}`;
}

function setStatus(message: string, tone: 'idle' | 'ok' | 'error' = 'idle') {
  const el = document.getElementById('sponsor-status');
  if (!el) return;
  el.textContent = message;
  el.className = tone;
}

function render(config: ChestReserveConfig) {
  document.body.innerHTML = `
    <main id="sponsor-page">
      <section class="sponsor-shell">
        <div class="sponsor-header">
          <div>
            <p class="eyebrow">CHEST RESERVE</p>
            <h1>${formatRaw(config.balanceRaw, config.decimals)} ${config.symbol}</h1>
          </div>
          <a class="back-link" href="/">Back</a>
        </div>

        <p class="sponsor-blurb">
          Top up the earmarked treasury balance from which chest TOKEN
          drops are paid out. When this hits zero, chests still drop
          points and hats — only the token line item pauses.
        </p>

        <div class="sponsor-meta">
          <span>${config.cluster}</span>
          <span>${config.symbol}</span>
        </div>

        <div id="preset-row" class="preset-row"></div>

        <label class="custom-field">
          <span>Custom ${config.symbol}</span>
          <input id="custom-token-amount" inputmode="decimal" placeholder="0.00" />
        </label>

        <button id="sponsor-submit" class="primary-btn" type="button" disabled>Fund reserve</button>
        <p id="sponsor-selected">Select an amount</p>
        <p id="sponsor-status" class="idle"></p>
      </section>
    </main>
  `;

  const style = document.createElement('style');
  style.textContent = `
    html, body {
      width: 100%;
      min-height: 100%;
      background: #05070c;
      color: #f4f0dc;
      font-family: Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
      overflow: auto;
    }
    #sponsor-page {
      min-height: 100vh;
      display: grid;
      place-items: center;
      padding: 28px 16px;
      background:
        radial-gradient(circle at 20% 18%, rgba(255, 209, 102, 0.16), transparent 30%),
        radial-gradient(circle at 88% 12%, rgba(255, 122, 0, 0.16), transparent 32%),
        linear-gradient(180deg, #090d16, #030408);
    }
    .sponsor-shell {
      width: min(520px, 100%);
      border: 1px solid rgba(255,255,255,0.12);
      background: rgba(9, 13, 22, 0.92);
      box-shadow: 0 24px 80px rgba(0,0,0,0.5);
      border-radius: 8px;
      padding: 24px;
    }
    .sponsor-header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 20px;
      margin-bottom: 14px;
    }
    .eyebrow {
      margin: 0 0 6px;
      color: #ffd166;
      font-size: 12px;
      font-weight: 800;
      letter-spacing: 0;
    }
    h1 {
      margin: 0;
      font-size: 34px;
      line-height: 1.05;
      letter-spacing: 0;
      color: #fff8d8;
    }
    .back-link {
      color: #a6b7d4;
      text-decoration: none;
      border: 1px solid rgba(255,255,255,0.12);
      border-radius: 6px;
      padding: 8px 10px;
      font-size: 13px;
    }
    .sponsor-blurb {
      margin: 0 0 14px;
      color: #c7d2e6;
      font-size: 13px;
      line-height: 1.45;
    }
    .sponsor-meta {
      display: flex;
      flex-wrap: wrap;
      gap: 8px;
      margin-bottom: 24px;
    }
    .sponsor-meta span {
      border: 1px solid rgba(255,255,255,0.1);
      border-radius: 999px;
      padding: 6px 9px;
      color: #c7d2e6;
      font-size: 12px;
    }
    .preset-row {
      display: grid;
      grid-template-columns: repeat(3, 1fr);
      gap: 10px;
      margin-bottom: 14px;
    }
    .preset-btn {
      min-height: 74px;
      border: 1px solid rgba(255, 209, 102, 0.32);
      background: rgba(255, 209, 102, 0.08);
      color: #f7ffe9;
      border-radius: 8px;
      cursor: pointer;
      font: inherit;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      gap: 5px;
    }
    .preset-btn strong {
      font-size: 20px;
      letter-spacing: 0;
    }
    .preset-btn span {
      font-size: 12px;
      color: #b7c6bf;
    }
    .preset-btn.selected {
      border-color: #ffd166;
      box-shadow: 0 0 0 2px rgba(255, 209, 102, 0.22);
    }
    .custom-field {
      display: flex;
      flex-direction: column;
      gap: 8px;
      color: #c9d4e7;
      font-size: 13px;
      margin-bottom: 16px;
    }
    .custom-field input {
      width: 100%;
      border: 1px solid rgba(255,255,255,0.13);
      background: rgba(255,255,255,0.05);
      color: #fff;
      border-radius: 8px;
      padding: 13px 14px;
      font: inherit;
      font-size: 18px;
    }
    .custom-field input:focus {
      outline: none;
      border-color: #ffaa00;
      box-shadow: 0 0 0 3px rgba(255, 170, 0, 0.18);
    }
    .primary-btn {
      width: 100%;
      min-height: 48px;
      border: 0;
      border-radius: 8px;
      background: #ffd166;
      color: #1a1408;
      font: inherit;
      font-weight: 900;
      cursor: pointer;
    }
    .primary-btn:disabled {
      cursor: not-allowed;
      opacity: 0.45;
    }
    #sponsor-selected {
      margin: 12px 0 0;
      color: #d8e0ef;
      min-height: 20px;
      font-size: 14px;
    }
    #sponsor-status {
      margin: 8px 0 0;
      min-height: 20px;
      font-size: 13px;
      color: #93a3bd;
    }
    #sponsor-status.ok { color: #14f195; }
    #sponsor-status.error { color: #ff7171; }
    @media (max-width: 520px) {
      .sponsor-shell { padding: 18px; }
      .preset-row { grid-template-columns: 1fr; }
      h1 { font-size: 28px; }
    }
  `;
  document.head.appendChild(style);
}

function bind(config: ChestReserveConfig) {
  let selectedRaw: bigint | null = null;
  const presetRow = document.getElementById('preset-row')!;
  const submit = document.getElementById('sponsor-submit') as HTMLButtonElement;
  const selected = document.getElementById('sponsor-selected')!;
  const custom = document.getElementById('custom-token-amount') as HTMLInputElement;

  const syncSelected = (raw: bigint | null) => {
    selectedRaw = raw;
    submit.disabled = !raw || raw <= 0n;
    selected.textContent = raw
      ? `${formatRaw(raw, config.decimals, 6)} ${config.symbol}`
      : 'Select an amount';
  };

  config.presets.forEach((preset) => {
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'preset-btn';
    btn.innerHTML = `<strong>${usdLabel(preset.usdMicro)}</strong><span>${formatRaw(preset.amountRaw, config.decimals, 4)} ${config.symbol}</span>`;
    btn.addEventListener('click', () => {
      for (const el of presetRow.querySelectorAll('.preset-btn')) el.classList.remove('selected');
      btn.classList.add('selected');
      custom.value = '';
      syncSelected(preset.amountRaw);
    });
    presetRow.appendChild(btn);
  });

  custom.addEventListener('input', () => {
    for (const el of presetRow.querySelectorAll('.preset-btn')) el.classList.remove('selected');
    syncSelected(parseTokenAmount(custom.value, config.decimals));
  });

  submit.addEventListener('click', async () => {
    if (!selectedRaw) return;
    submit.disabled = true;
    try {
      const progressLabels: Record<string, string> = {
        building: 'Preparing transaction',
        awaiting_signature: 'Waiting for wallet signature',
        confirming: 'Confirming on-chain',
        finalizing: 'Finalizing transaction',
      };
      const paid = await paySponsorPoolNative(selectedRaw, config, (status) => {
        setStatus(progressLabels[status] ?? status);
      });
      setStatus('Verifying payment');
      const verified = await verifyChestReservePayment({
        signature: paid.signature,
        walletAddress: paid.walletAddress,
        amountRaw: selectedRaw,
      });
      setStatus(
        `Funded ${formatRaw(verified.amountRaw, verified.decimals, 6)} ${verified.symbol}. Reserve: ${formatRaw(verified.balanceRaw, verified.decimals, 4)} ${verified.symbol}`,
        'ok',
      );
      syncSelected(null);
      custom.value = '';
      for (const el of presetRow.querySelectorAll('.preset-btn')) el.classList.remove('selected');
    } catch (err: any) {
      setStatus(err?.message ?? 'Sponsor payment failed', 'error');
      submit.disabled = !selectedRaw;
    }
  });
}

export function initSponsorChestPage(): boolean {
  if (window.location.pathname !== '/sponsor-chest-reserve') return false;
  document.title = 'Fund Chest Reserve - Pump My Snake';
  document.body.innerHTML = '<main id="sponsor-page"><section class="sponsor-shell"><p>Loading reserve...</p></section></main>';
  fetchChestReserveConfig()
    .then((config) => {
      render(config);
      bind(config);
    })
    .catch((err) => {
      document.body.innerHTML = `<main id="sponsor-page"><section class="sponsor-shell"><p>${err?.message ?? 'Unable to load chest reserve'}</p><p><a class="back-link" href="/">Back</a></p></section></main>`;
    });
  return true;
}
